import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';
import {
  FileText,
  Download,
  Trash2,
  Shield,
  FileSpreadsheet,
  Image as ImageIcon,
  File,
  FileIcon
} from 'lucide-react';
import { useCallback } from 'react';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { DropdownButton, useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';

// ============================================
// TYPES
// ============================================

type DocumentType =
  | 'ID_CARD'           // Carte d'identité
  | 'PASSPORT'          // Passeport
  | 'KBIS'              // Extrait Kbis
  | 'PAYSLIP'           // Fiche de paie
  | 'INSURANCE_PRODUCT' // Fiche produit assurance
  | 'CONTRACT'          // Contrat
  | 'INVOICE'           // Facture
  | 'RIB'               // RIB
  | 'MEDICAL_REPORT'    // Rapport médical
  | 'CLAIM_FORM'        // Formulaire de sinistre
  | 'OTHER';            // Autre

type DocumentStatus =
  | 'PENDING_VALIDATION'  // En attente de validation
  | 'VALIDATED'           // Validé
  | 'REJECTED'            // Rejeté
  | 'EXPIRED'             // Expiré
  | 'ARCHIVED';           // Archivé

type FileType = 'PDF' | 'JPEG' | 'PNG' | 'XLSX' | 'DOCX' | 'OTHER';

interface Document {
  id: number;
  fileName: string;
  documentType: DocumentType;
  fileType: FileType;
  fileSize: number; // en octets
  status: DocumentStatus;
  createdAt: string;
  updatedAt: string;
  expirationDate?: string;
  uploadedBy: {
    id: number;
    name: string;
    role: string;
  };
  relatedTo: {
    type: 'INSURED' | 'CONTRACT' | 'CLAIM' | 'PRODUCT';
    id: number;
    name: string;
  };
  tags: string[];
  validatedBy?: {
    name: string;
    date: string;
  };
  rejectionReason?: string;
}

// ============================================
// DONNÉES MOCKÉES
// ============================================

const documentTypeLabels: Record<DocumentType, string> = {
  ID_CARD: "Carte d'identité",
  PASSPORT: 'Passeport',
  KBIS: 'Extrait Kbis',
  PAYSLIP: 'Fiche de paie',
  INSURANCE_PRODUCT: "Fiche produit",
  CONTRACT: 'Contrat',
  INVOICE: 'Facture',
  RIB: 'RIB',
  MEDICAL_REPORT: 'Rapport médical',
  CLAIM_FORM: 'Formulaire de sinistre',
  OTHER: 'Autre'
};

const statusLabels: Record<DocumentStatus, string> = {
  PENDING_VALIDATION: 'En attente',
  VALIDATED: 'Validé',
  REJECTED: 'Rejeté',
  EXPIRED: 'Expiré',
  ARCHIVED: 'Archivé'
};

const statusColors: Record<DocumentStatus, string> = {
  PENDING_VALIDATION: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  VALIDATED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
  EXPIRED: 'bg-orange-100 text-orange-800 border-orange-200',
  ARCHIVED: 'bg-gray-100 text-gray-800 border-gray-200'
};

// Génération de documents mockés
const generateMockDocuments = (count: number): Document[] => {
  const documentTypes: DocumentType[] = [
    'ID_CARD', 'PASSPORT', 'KBIS', 'PAYSLIP', 'INSURANCE_PRODUCT',
    'CONTRACT', 'INVOICE', 'RIB', 'MEDICAL_REPORT', 'CLAIM_FORM'
  ];

  const fileTypes: FileType[] = ['PDF', 'JPEG', 'PNG', 'XLSX', 'DOCX'];
  const statuses: DocumentStatus[] = ['PENDING_VALIDATION', 'VALIDATED', 'REJECTED', 'EXPIRED', 'ARCHIVED'];

  const insuredNames = ['Jean Dupont', 'Marie Martin', 'Pierre Dubois', 'Sophie Laurent', 'Luc Bernard'];
  const uploaderNames = ['Agent Smith', 'Alice Cooper', 'Bob Wilson', 'Carol White', 'David Brown'];

  return Array.from({ length: count }, (_, i) => {
    const docType = documentTypes[Math.floor(Math.random() * documentTypes.length)];
    const fileType = fileTypes[Math.floor(Math.random() * fileTypes.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];

    const createdAt = new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1);
    const updatedAt = new Date(createdAt.getTime() + Math.random() * 30 * 24 * 60 * 60 * 1000);

    const hasExpiration = ['ID_CARD', 'PASSPORT', 'KBIS', 'MEDICAL_REPORT'].includes(docType);
    const expirationDate = hasExpiration
      ? new Date(2025, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString()
      : undefined;

    const validated = status === 'VALIDATED';
    const rejected = status === 'REJECTED';

    return {
      id: i + 1,
      fileName: `${documentTypeLabels[docType].replace(/['\s]/g, '_')}_${i + 1}.${fileType.toLowerCase()}`,
      documentType: docType,
      fileType,
      fileSize: Math.floor(Math.random() * 5000000) + 100000, // Entre 100KB et 5MB
      status,
      createdAt: createdAt.toISOString(),
      updatedAt: updatedAt.toISOString(),
      expirationDate,
      uploadedBy: {
        id: Math.floor(Math.random() * 5) + 1,
        name: uploaderNames[Math.floor(Math.random() * uploaderNames.length)],
        role: Math.random() > 0.5 ? 'Agent' : 'Gestionnaire'
      },
      relatedTo: {
        type: Math.random() > 0.5 ? 'INSURED' : 'CONTRACT',
        id: Math.floor(Math.random() * 100) + 1,
        name: insuredNames[Math.floor(Math.random() * insuredNames.length)]
      },
      tags: ['important', 'urgent', 'à_vérifier'].slice(0, Math.floor(Math.random() * 3) + 1),
      validatedBy: validated ? {
        name: 'Validateur ' + Math.floor(Math.random() * 3 + 1),
        date: updatedAt.toISOString()
      } : undefined,
      rejectionReason: rejected ? 'Document illisible' : undefined
    };
  });
};

const mockDocuments = generateMockDocuments(150);

// API Mock
const mockFetchDocuments = async (params: any) => {
  // Simuler un délai réseau
  await new Promise(resolve => setTimeout(resolve, 800));

  let filtered = [...mockDocuments];

  // Recherche
  if (params.searchString) {
    const search = params.searchString.toLowerCase();
    filtered = filtered.filter(doc =>
      doc.fileName.toLowerCase().includes(search) ||
      documentTypeLabels[doc.documentType].toLowerCase().includes(search) ||
      doc.relatedTo.name.toLowerCase().includes(search)
    );
  }

  // Filtres
  if (params.filters) {
    if (params.filters.documentType) {
      filtered = filtered.filter(doc => doc.documentType === params.filters.documentType);
    }
    if (params.filters.status) {
      filtered = filtered.filter(doc => doc.status === params.filters.status);
    }
    if (params.filters.fileType) {
      filtered = filtered.filter(doc => doc.fileType === params.filters.fileType);
    }
  }

  // Tri
  if (params.sorting) {
    const [field, direction] = params.sorting.split(' ');
    filtered.sort((a: any, b: any) => {
      if (a[field] < b[field]) return direction === 'asc' ? -1 : 1;
      if (a[field] > b[field]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Pagination
  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / params.pageSize);
  const start = (params.pageNumber - 1) * params.pageSize;
  const items = filtered.slice(start, start + params.pageSize);

  return {
    items,
    totalItems,
    totalPages,
    currentPage: params.pageNumber
  };
};

const mockFetchFilters = async () => {
  await new Promise(resolve => setTimeout(resolve, 300));

  return [
    {
      groupName: 'Type de document',
      filters: Object.entries(documentTypeLabels).map(([key, label]) => ({
        key: 'documentType',
        label,
        type: 'select' as const,
        options: [{ value: key, label }]
      }))
    },
    {
      groupName: 'Statut',
      filters: Object.entries(statusLabels).map(([key, label]) => ({
        key: 'status',
        label,
        type: 'select' as const,
        options: [{ value: key, label }]
      }))
    },
    {
      groupName: 'Type de fichier',
      filters: ['PDF', 'JPEG', 'PNG', 'XLSX', 'DOCX'].map(type => ({
        key: 'fileType',
        label: type,
        type: 'select' as const,
        options: [{ value: type, label: type }]
      }))
    }
  ];
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export function DocumentsView() {
  // Utilitaires
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getFileIcon = (fileType: FileType) => {
    switch (fileType) {
      case 'PDF':
        return <FileText className="text-red-500" size={18} />;
      case 'XLSX':
        return <FileSpreadsheet className="text-green-500" size={18} />;
      case 'JPEG':
      case 'PNG':
        return <ImageIcon className="text-blue-500" size={18} />;
      default:
        return <File className="text-gray-500" size={18} />;
    }
  };

  const isExpiringSoon = (expirationDate?: string): boolean => {
    if (!expirationDate) return false;
    const daysUntilExpiration = Math.floor(
      (new Date(expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiration <= 30 && daysUntilExpiration > 0;
  };

  const isExpired = (expirationDate?: string): boolean => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  // Actions
  const handleView = (doc: Document) => {
    console.log('Visualiser le document:', doc);
    // Ouvrir une modale ou naviguer vers une page de détail
  };

  const handleDownload = (doc: Document) => {
    console.log('Télécharger:', doc.fileName);
    // Logique de téléchargement
  };

  const handleEdit = (doc: Document) => {
    console.log('Éditer:', doc);
    // Ouvrir un formulaire d'édition
  };

  const handleDelete = (doc: Document) => {
    // Utiliser une modale custom au lieu de confirm
    console.log('Supprimer:', doc);
    // TODO: Ouvrir une modale de confirmation
    // if (showConfirmDialog(`Êtes-vous sûr de vouloir supprimer "${doc.fileName}" ?`)) {
    //   // Logique de suppression
    // }
  };

  const handleBulkDownload = (docs: Document[]) => {
    console.log('Téléchargement groupé de', docs.length, 'documents');
    // Logique de téléchargement en masse
  };

  const handleBulkDelete = (docs: Document[]) => {
    console.log('Suppression groupée de', docs.length, 'documents');
    // TODO: Ouvrir une modale de confirmation
    // if (showConfirmDialog(`Supprimer ${docs.length} documents ?`)) {
    //   // Logique de suppression
    // }
  };

  const handleUpload = () => {
    console.log('Ouvrir le formulaire d\'upload');
    // Ouvrir un dialogue d'upload
  };

  const { createMenuItem } = useActionMenu();

  const createMenuItems = useCallback((doc: Document) => [
    createMenuItem(
      'download',
      'Télécharger',
      () => handleDownload(doc),
      { icon: 'download' },
    ),
    createMenuItem(
      'edit',
      'Modifier',
      () => handleEdit(doc),
      { icon: 'edit', disabled: doc.status === 'ARCHIVED' },
    ),
    createMenuItem(
      'delete',
      'Supprimer',
      () => handleDelete(doc),
      { icon: 'trash', variant: 'danger', disabled: doc.status === 'VALIDATED' },
    ),
  ], [createMenuItem]);

  return (
    <SimpleDashboardContent>
      {/* Identité de page admin */}
      <GenericViewHeader title="Documents" icon={FileIcon} />
      {/* Indicateurs en haut de page */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-primary">
            {mockDocuments.length}
          </div>
          <div className="text-sm text-gray-600">Documents totaux</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-yellow-600">
            {mockDocuments.filter(d => d.status === 'PENDING_VALIDATION').length}
          </div>
          <div className="text-sm text-gray-600">En attente</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-orange-600">
            {mockDocuments.filter(d => isExpiringSoon(d.expirationDate)).length}
          </div>
          <div className="text-sm text-gray-600">Expirent bientôt</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-600">
            {mockDocuments.filter(d => isExpired(d.expirationDate)).length}
          </div>
          <div className="text-sm text-gray-600">Expirés</div>
        </div>
      </div>

      <ServerTable<Document>
        // Configuration API
        apiConfig={{
          fetchData: mockFetchDocuments,
          fetchFilters: mockFetchFilters
        }}

        // Colonnes
        columns={[
          {
            key: 'fileName',
            label: 'Nom du fichier',
            sortable: true,
            width: '300px',
            render: (value: any, row: Document) => (
              <div className="flex items-center gap-3">
                {getFileIcon(row.fileType)}
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900 truncate max-w-xs">
                    {String(value)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatFileSize(row.fileSize)}
                  </span>
                </div>
              </div>
            )
          },
          {
            key: 'documentType',
            label: 'Type de document',
            sortable: true,
            render: (value: any) => (
              <div className="flex items-center gap-2">
                <Shield className="text-primary" size={16} />
                <span className="text-sm font-medium text-gray-700">
                  {documentTypeLabels[value as DocumentType]}
                </span>
              </div>
            )
          },
          {
            key: 'status',
            label: 'Statut',
            sortable: true,
            align: 'center',
            render: (value: any, row: Document) => (
              <div className="flex flex-col items-center gap-1">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[value as DocumentStatus]}`}>
                  {statusLabels[value as DocumentStatus]}
                </span>
                {row.expirationDate && (
                  <div className="text-xs">
                    {isExpired(row.expirationDate) && (
                      <span className="text-red-600 font-medium">⚠️ Expiré</span>
                    )}
                    {isExpiringSoon(row.expirationDate) && !isExpired(row.expirationDate) && (
                      <span className="text-orange-600 font-medium">⏰ Expire bientôt</span>
                    )}
                  </div>
                )}
              </div>
            )
          },
          {
            key: 'relatedTo',
            label: 'Lié à',
            render: (value: any) => {
              const relatedTo = value as Document['relatedTo'];
              return (
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900">
                    {relatedTo.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {relatedTo.type === 'INSURED' ? 'Assuré' :
                      relatedTo.type === 'CONTRACT' ? 'Contrat' :
                        relatedTo.type === 'CLAIM' ? 'Sinistre' : 'Produit'} #{relatedTo.id}
                  </span>
                </div>
              );
            }
          },
          {
            key: 'uploadedBy',
            label: 'Téléchargé par',
            render: (value: any) => {
              const uploadedBy = value as Document['uploadedBy'];
              return (
                <div className="flex flex-col">
                  <span className="text-sm text-gray-900">{uploadedBy.name}</span>
                  <span className="text-xs text-gray-500">{uploadedBy.role}</span>
                </div>
              );
            }
          },
          {
            key: 'createdAt',
            label: 'Date de création',
            sortable: true,
            render: (value: any) => (
              <div className="flex flex-col">
                <span className="text-sm text-gray-900">
                  {formatDate(String(value))}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(String(value)).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )
          },
          {
            key: 'expirationDate',
            label: 'Date d\'expiration',
            sortable: true,
            align: 'center',
            render: (value: any) => {
              const dateStr = value as string | undefined;
              return dateStr ? (
                <span className={`text-sm ${isExpired(dateStr) ? 'text-red-600 font-bold' :
                  isExpiringSoon(dateStr) ? 'text-orange-600 font-semibold' :
                    'text-gray-700'
                  }`}>
                  {formatDate(dateStr)}
                </span>
              ) : (
                <span className="text-gray-400 text-sm">—</span>
              );
            }
          }
        ]}

        // Le titre est porté par le header d'identité admin

        // Actions
        actions={{
          create: {
            label: 'Importer un document',
            onClick: handleUpload
          },
          row: [
            {
              label: 'seeMore',
              render: (doc: Document, index: number) => (
                <Button key={index} color='slate' round onClick={() => handleView(doc)}>
                  <EyeIcon />
                </Button>
              ),
            },
            {
              label: 'moreMenu',
              render: (doc: Document, index: number) => (
                <DropdownButton
                  key={index}
                  width='w-56'
                  anchor='bottom'
                  items={createMenuItems(doc)}
                />
              ),
            },
          ],
          bulk: [
            {
              label: 'Télécharger la sélection',
              icon: <Download size={16} />,
              onClick: handleBulkDownload
            },
            {
              label: 'Supprimer la sélection',
              icon: <Trash2 size={16} />,
              onClick: handleBulkDelete,
              variant: 'danger'
            }
          ]
        }}

        // Configuration
        selectable
        pageSize={15}
        pageSizeOptions={[10, 15, 20, 30, 50]}
        showPaginationInfo

        // Recherche et filtres
        enableSearch
        enableFilters
        searchPlaceholder="Rechercher un document (nom, type, assuré...)"

        // Skeleton
        skeletonRowCount={10}

        // Callbacks
        onRowClick={handleView}
      />
    </SimpleDashboardContent>
  );
}
